import React, { useEffect, useRef, useState } from "react";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  queryIsLoading,
  useGetFootprintingCropPlantProtection,
  useGetFootprintingCropPrimaryDataById,
  useGetPracticePlantProtections,
  useMutateFootprintingCropPrimaryData,
  useMutateFootprintingFarmCropPlantProtection,
  useTranslation,
} from "utils";

import { ENUM_PLANT_PROTECTION_UNITS, PRACTICE_SLUG, UploadablePlantProtection } from "models";

import {
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputNumberWithUnitSelector,
  KlimRadioGroup,
  Option,
  PageSpin,
  Select,
  Tooltip,
} from "components";

type CropPlantProtectionProps = {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  cropId: string;
  year: number;
};

const plantProtectionPlaceholder: UploadablePlantProtection = {
  id: undefined,
  practicesPlantProtectionId: "",
  amount: null,
  amountUnit: null,
};

export const CropPlantProtectionForm = ({ primaryDataId, readOnly, farmId, cropId, year }: CropPlantProtectionProps) => {
  const formRef = useRef<FormInstance>(null);
  const { t } = useTranslation(["footprinting", "practices"]);

  const [plantProtectionActive, setPlantProtectionActive] = useState<boolean | null>(null);
  const [appliedPlantProtections, setAppliedPlantProtections] = useState<UploadablePlantProtection[]>([]);

  const cropPrimaryDataQuery = useGetFootprintingCropPrimaryDataById(primaryDataId ?? "");

  const plantProtectionQuery = useGetFootprintingCropPlantProtection(primaryDataId);
  const practicePlantProtectionQuery = useGetPracticePlantProtections();

  const selectGroupName = "footprinting-farm-plant-protection-select_plant-protection";

  const { mutate: mutateFarmCropPlantProtection } = useMutateFootprintingFarmCropPlantProtection(
    farmId,
    cropId,
    year,
    {
      footprintingFarmPrimaryDataId: primaryDataId ?? "",
      selectedPlantProtection: appliedPlantProtections.filter((p) => !!p.practicesPlantProtectionId),
    },
    primaryDataId
  );

  const { mutate: mutateCropPrimaryData } = useMutateFootprintingCropPrimaryData(
    farmId,
    cropId,
    year,
    {
      farmId,
      cropId,
      year,
      plantProtectionActive,
    },
    primaryDataId
  );

  const isLoading = [cropPrimaryDataQuery, plantProtectionQuery, practicePlantProtectionQuery].some(queryIsLoading);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateCropPrimaryData();
        mutateFarmCropPlantProtection();
      }
    },
    [isLoading, mutateCropPrimaryData, mutateFarmCropPlantProtection, readOnly]
  );

  useEffect(() => {
    if (plantProtectionQuery.data) {
      setAppliedPlantProtections(
        plantProtectionQuery.data.length
          ? plantProtectionQuery.data.map((p) => ({
              id: p.id,
              practicesPlantProtectionId: p.practicesPlantProtectionId ?? "",
              amount: p.amount ?? null,
              amountUnit: p.amountUnit ?? null,
            }))
          : [{ ...plantProtectionPlaceholder }]
      );
    }
  }, [plantProtectionQuery.data]);

  // Note - this will only run once because the query above is only the GET
  useEffect(() => {
    if (cropPrimaryDataQuery.data && cropPrimaryDataQuery.isSuccess) {
      setPlantProtectionActive(cropPrimaryDataQuery.data.plantProtectionActive ?? null);

      formRef.current?.setFieldsValue({
        plantProtectionActive: cropPrimaryDataQuery.data.plantProtectionActive ?? null,
      });

      if (cropPrimaryDataQuery.data.plantProtectionActive !== null) {
        formRef.current?.validateFields();
      }
    }
  }, [cropPrimaryDataQuery.data, cropPrimaryDataQuery.isSuccess]);

  // This ensures that all form fields rerender when the non-last protection
  // is removed (i.e. they appear to slide up)
  useEffect(() => {
    if (appliedPlantProtections.length > 0) {
      formRef.current?.resetFields();
    }
  }, [appliedPlantProtections.length]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <div>
      <h1 className="center margin-bottom-200">{t("cropPlantProtectionForm.title")}</h1>
      <Card className="padding-100 margin-bottom-100">
        <Flex className="padding-100" gap="1rem" vertical>
          <p className="paragraph-m margin-0">{t("cropPlantProtectionForm.info")}</p>
        </Flex>
      </Card>
      <Card dataTestId="footprinting-crop-plant-protection-form" className="margin-bottom-100 padding-100">
        <Flex align="center" gap={16} justify="space-between" className="padding-100">
          <span style={{ maxWidth: "64%" }}>
            {t("cropPlantProtectionForm.question.plantProtectionQuestionLabel")}
            {true && <span className="required-asterisk">*</span>}
            <Tooltip title={t("cropPlantProtectionForm.tooltips.plantProtectionQuestionLabel")}>
              <Icon size="s" iconName="tabler/info-circle" className="input-label-info-icon" />
            </Tooltip>
          </span>
          <KlimRadioGroup
            required
            name="plantProtectionActive"
            dataTestId="footprinting-crop-plant-protection-radio"
            disabled={readOnly}
            vertical={false}
            type="tertiary"
            size="large"
            onChange={({ target }) => {
              setPlantProtectionActive(target.value);
            }}
            value={plantProtectionActive}
            defaultValue={plantProtectionActive}
            optionType="button"
            options={[true, false].map((opt) => ({
              label: t(`radioAnswer.${opt}`),
              value: opt,
            }))}
          />
        </Flex>
        <Form ref={formRef}>
          {plantProtectionActive &&
            appliedPlantProtections.map((protection, index) => {
              const selectGroupWithIndex = `${selectGroupName}-${index}`;
              const updateItem = (item: UploadablePlantProtection) => {
                const next = [...appliedPlantProtections];
                next[index] = { ...item };
                setAppliedPlantProtections(next);
              };

              return (
                <Form.Item key={index} className="padding-100">
                  <Flex className="margin-bottom-100" justify="space-between" align="center" vertical={false}>
                    <h5 style={{ flex: 1 }}>{t("cropPlantProtectionForm.question.applicationNumber", { count: index + 1 })}</h5>
                    <IconButton
                      type="tertiary"
                      disabled={readOnly}
                      size="small"
                      iconName="tabler/trash"
                      onClick={() => setAppliedPlantProtections([...appliedPlantProtections].filter((_, i) => i !== index))}
                    />
                  </Flex>
                  {/* @ts-expect-error need to convert to tsx */}
                  <Select
                    disabled={readOnly}
                    className="margin-bottom-100"
                    name={selectGroupWithIndex}
                    dataTestId={selectGroupWithIndex}
                    label={t("cropPlantProtectionForm.question.plantProtectionTypeQuestionLabel")}
                    tooltip={t("cropPlantProtectionForm.tooltips.plantProtectionTypeQuestionLabel")}
                    rules={[{ required: true, message: false }]}
                    initialValue={practicePlantProtectionQuery.data?.find((p) => p.id === protection.practicesPlantProtectionId)?.name || ""}
                    value={practicePlantProtectionQuery.data?.find((p) => p.id === protection.practicesPlantProtectionId)?.name || ""}
                    onChange={(v) => {
                      const matchingPractice = practicePlantProtectionQuery.data?.find((p) => p.name === v);
                      updateItem({
                        ...protection,
                        practicesPlantProtectionId: matchingPractice ? matchingPractice.id : "",
                      });
                    }}
                    dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
                  >
                    {(practicePlantProtectionQuery.data ?? []).map((practice) => (
                      <Option
                        disabled={readOnly}
                        key={`${selectGroupWithIndex}-${practice.id}`}
                        data-testid={`${selectGroupWithIndex}-${practice.id}`}
                        value={practice.name}
                      >
                        {t(`${PRACTICE_SLUG.PLANT_PROTECTIONS}.${practice.translationSlug}`, { ns: "practices" }) || practice.name}
                      </Option>
                    ))}
                  </Select>

                  <InputNumberWithUnitSelector
                    className="floating-label margin-bottom-200"
                    disabled={readOnly}
                    dataTestId={`footprinting-crop-plant-protection-amount-input-${index}`}
                    name={`amountAppliedKgPerHa-${index}`}
                    label={t("cropPlantProtectionForm.question.amountAppliedQuestionLabel")}
                    tooltip={t("cropPlantProtectionForm.tooltips.amountAppliedQuestionLabel")}
                    unitLabel={t("unit", { ns: "footprinting" })}
                    units={["kilograms", "liters"]}
                    value={protection.amount}
                    initialValue={protection.amount}
                    unitValue={protection.amountUnit}
                    onChange={(v, unit) => updateItem({ ...protection, amount: v, amountUnit: unit as ENUM_PLANT_PROTECTION_UNITS })}
                    rules={[
                      { required: true },
                      { type: "number" },
                      {
                        validator: (_: unknown, v: number) => {
                          if (isNullOrUndefined(v) || isValidNonnegativeNumber(v)) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error(t("cropPlantProtectionForm.error.amountAppliedLabelError")));
                        },
                      },
                    ]}
                  />
                </Form.Item>
              );
            })}
          {plantProtectionActive && (
            <Button
              type="text"
              disabled={readOnly}
              onClick={() => {
                setAppliedPlantProtections([...appliedPlantProtections, { ...plantProtectionPlaceholder }]);
              }}
            >
              <Icon iconName="klim/plus" className="icon inline" /> {t("cropPlantProtectionForm.add")}
            </Button>
          )}
        </Form>
      </Card>
    </div>
  );
};
